/**
 * URL privacy for analytics — CON-154.
 * ---------------------------------------------------------------------------
 * Nonprofits can hand a donor a link that pre-fills the form: `?email=…&firstname=…
 * &address=…` on a hosted page, or the same names on a merchant's page, which the
 * embed script (`donation-elements-backend/hosted-scripts/url-params.js`) forwards
 * to the widget iframe as `cs_email=…`, `cs_firstname=…` (and, per form,
 * `<formID>.email=…`). PostHog stamps the page URL on EVERY capture as
 * `$current_url` (and the host page as `$referrer`, and both again as
 * `$initial_*` person properties), so a prefilled donor's email and street address
 * were reaching the analytics project on every event the page emitted.
 *
 * This module is the one place that knows which query parameters exist and which
 * of them carry donor data. `useUrlPrefill` (donation-elements) reads its
 * allowlist (PREFILL_QUERY_PARAMS) from here, and `getPostHogConfig` scrubs every
 * URL-shaped property through here (DONOR_PII_QUERY_PARAMS) before an event
 * leaves the page. The two lists are kept honest by a test that requires every
 * prefill name to be classified as PII or explicitly not — a new field cannot be
 * added to the allowlist without a decision about masking it.
 *
 * Values are REPLACED with PostHog's own `<masked>` token rather than removed:
 * "a prefill link was used" stays visible; what was in it does not.
 */

/** Every query parameter the donor apps accept for pre-filling (the hook's allowlist). */
export const PREFILL_QUERY_PARAMS = Object.freeze([
  'amount',
  'fund',
  'frequency',
  'campaign',
  'sponsorshipgroup',
  'sponsorshipitem',
  'quantity',
  'firstname',
  'lastname',
  'organizationname',
  'email',
  'phone',
  'address',
  'address2',
  'city',
  'state',
  'zip',
  'country',
  'defaultscheduledrangeoverride',
]);

/** The subset of PREFILL_QUERY_PARAMS whose values identify or locate a person. */
export const DONOR_PII_QUERY_PARAMS = Object.freeze([
  'firstname',
  'lastname',
  'organizationname',
  'email',
  'phone',
  'address',
  'address2',
  'city',
  'state',
  'zip',
  'country',
]);

/**
 * The prefill parameters that are NOT donor data — what a link may suggest about
 * the gift, never about the giver. Every name in PREFILL_QUERY_PARAMS must be in
 * exactly one of this list and DONOR_PII_QUERY_PARAMS; the unit test enforces it,
 * so adding a prefill field without deciding which side it falls on fails CI.
 */
export const NON_PII_PREFILL_QUERY_PARAMS = Object.freeze([
  'amount',
  'fund',
  'frequency',
  'campaign',
  'sponsorshipgroup',
  'sponsorshipitem',
  'quantity',
  'defaultscheduledrangeoverride',
]);

/** The token PostHog itself uses when it masks a query value. */
export const MASKED = '<masked>';

/**
 * Prefixes and shapes that also carry donor-entered text and must be masked:
 * custom-input answers (`ci_*`, `gci_*`) and per-seat attendee fields
 * (`seat1_firstname`, `seat2_email`, …). Both travel bare on hosted pages and
 * `cs_`-prefixed on the widget iframe.
 */
const FREE_TEXT_PREFIXES = ['ci_', 'gci_'];
const SEAT_FIELD = /^seat\d+_/;

const PII_SET = new Set(DONOR_PII_QUERY_PARAMS);

/**
 * `true` when a query-parameter NAME is one whose value must not reach analytics.
 * Handles the widget's `cs_` prefix and the embed script's `<formID>.field` form.
 */
export function isDonorPiiParam(name) {
  if (typeof name !== 'string' || !name) return false;
  let key = name.toLowerCase();
  if (key.startsWith('cs_')) key = key.slice(3);
  const dot = key.indexOf('.');
  if (dot > 0) key = key.slice(dot + 1);
  if (PII_SET.has(key)) return true;
  if (FREE_TEXT_PREFIXES.some((p) => key.startsWith(p))) return true;
  return SEAT_FIELD.test(key);
}

/**
 * Mask donor-data query values in a URL string. Anything that is not a string,
 * has no query, or cannot be parsed comes back unchanged — this function never
 * throws and never drops a URL.
 */
export function maskDonorPiiInUrl(url) {
  if (typeof url !== 'string' || !url.includes('?')) return url;
  try {
    // Absolute URLs parse directly; relative ones parse against a throwaway base
    // and are re-relativised below.
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
    const parsed = new URL(url, absolute ? undefined : 'http://relative.invalid');
    let touched = false;
    for (const [key, value] of [...parsed.searchParams.entries()]) {
      if (isDonorPiiParam(key) && value !== MASKED) {
        parsed.searchParams.set(key, MASKED);
        touched = true;
      }
    }
    // A hash router puts its query inside the fragment (`#/give?email=…`); the
    // SDK's own masker re-appends the fragment untouched, so this one looks there too.
    const hashQuery = parsed.hash.indexOf('?');
    if (hashQuery !== -1) {
      const fragmentParams = new URLSearchParams(parsed.hash.slice(hashQuery + 1));
      let fragmentTouched = false;
      for (const [key, value] of [...fragmentParams.entries()]) {
        if (isDonorPiiParam(key) && value !== MASKED) {
          fragmentParams.set(key, MASKED);
          fragmentTouched = true;
        }
      }
      if (fragmentTouched) {
        parsed.hash = `${parsed.hash.slice(0, hashQuery + 1)}${fragmentParams.toString()}`;
        touched = true;
      }
    }
    if (!touched) return url;
    // `URLSearchParams` serialises `<masked>` as `%3Cmasked%3E`; keep PostHog's
    // literal token so the two masking paths read the same in the UI.
    const out = parsed.toString().replace(/%3Cmasked%3E/g, MASKED);
    return absolute ? out : out.replace(/^http:\/\/relative\.invalid/, '');
  } catch {
    return url;
  }
}

/**
 * Receipt links (CON-261): `/portal/r/<code>` and `/portal/receipt/<id>?t=<signature>`.
 * The code and the signature are bearer secrets for one gift's receipt and its
 * claim, so neither may reach analytics. The router matches without case, so
 * neither does this. Only a path that IS a receipt address is touched.
 */
const RECEIPT_PATH = /^((?:\/portal)?\/(?:r|receipt)\/)([^/]+)(\/?)$/i;

/**
 * Where the donor portal is served in production (`https://charitystack.com/portal/…`).
 * The one place a host is named: the portal app adds its own page host at runtime
 * (`portalScrubOptions`), which covers the dev Amplify host and localhost without
 * a literal. Every other host — a third-party referrer, another app's URL — is
 * never rewritten (CON-261 review round 2: `reddit.com/r/…` must stay intact).
 */
export const PRODUCTION_PORTAL_HOSTS = Object.freeze(['charitystack.com', 'www.charitystack.com']);

/** `true` when a pathname is a portal receipt address (`/portal/r/…`, `/portal/receipt/…`). */
export function isReceiptPath(pathname) {
  return typeof pathname === 'string' && RECEIPT_PATH.test(pathname);
}

/**
 * Replace a receipt address's code (path segment) and signature (`t`) with
 * `<masked>`, but only on a CharityStack portal host:
 * - an absolute URL whose host is in `portalHosts`;
 * - a host-less path (`$pathname`) only when `currentAppIsPortal`, since a path
 *   names the page the event came from.
 * Anything else, or anything that fails to parse, comes back byte-identical;
 * never throws.
 */
export function redactReceiptSecretsInUrl(url, { portalHosts = PRODUCTION_PORTAL_HOSTS, currentAppIsPortal = false } = {}) {
  if (typeof url !== 'string' || !/\/(?:r|receipt)\//i.test(url)) return url;
  try {
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
    if (!absolute && !(currentAppIsPortal && url.startsWith('/'))) return url;
    const parsed = new URL(url, absolute ? undefined : 'http://relative.invalid');
    if (absolute && !portalHosts.includes(parsed.host.toLowerCase())) return url;
    const match = RECEIPT_PATH.exec(parsed.pathname);
    if (!match) return url;
    parsed.pathname = `${match[1]}${MASKED}${match[3]}`;
    if (parsed.searchParams.has('t')) parsed.searchParams.set('t', MASKED);
    const out = parsed.toString().replace(/%3Cmasked%3E/gi, MASKED);
    return absolute ? out : out.replace(/^http:\/\/relative\.invalid/, '');
  } catch {
    return url;
  }
}

/**
 * The receipt-scrub options for the donor portal app: its own page host joins
 * the production hosts, and its host-less paths are scrubbed too. Read at each
 * call, so it always matches the page the event came from.
 */
export function portalScrubOptions() {
  const host = typeof window !== 'undefined' ? window.location?.host?.toLowerCase() : '';
  return {
    portalHosts: host ? [...PRODUCTION_PORTAL_HOSTS, host] : [...PRODUCTION_PORTAL_HOSTS],
    currentAppIsPortal: true,
  };
}

/** Property names PostHog fills with a page URL, on the event and as person properties. */
const URL_PROPERTY_KEYS = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$prev_pageview_current_url',
];

function looksLikeUrlKey(key) {
  const k = key.toLowerCase();
  return URL_PROPERTY_KEYS.includes(key) || k.endsWith('_url') || k.endsWith('url') || k.endsWith('referrer')
    // `$pathname`, `$session_entry_pathname`, `$initial_pathname`: a receipt
    // code sits in the path, with no query string at all.
    || k.endsWith('pathname');
}

/** Scrub every key of an object that is keyed by page URL; SAME object when nothing changed. */
function maskUrlKeys(bag, receiptOptions) {
  let changed = false;
  const out = {};
  for (const [key, value] of Object.entries(bag)) {
    const next = redactReceiptSecretsInUrl(key.includes('?') ? maskDonorPiiInUrl(key) : key, receiptOptions);
    if (next !== key) changed = true;
    out[next] = out[next] && Array.isArray(out[next]) && Array.isArray(value) ? [...out[next], ...value] : value;
  }
  return changed ? out : bag;
}

/**
 * Mask donor-data query values in every URL-shaped property of a properties bag,
 * recursing one level into PostHog's `$set` / `$set_once` person-property bags.
 * Returns a NEW object when something changed and the SAME object when nothing did.
 */
export function maskDonorPiiInProperties(properties, receiptOptions) {
  if (!properties || typeof properties !== 'object') return properties;
  let out = null;
  for (const [key, value] of Object.entries(properties)) {
    let next = value;
    if (typeof value === 'string' && looksLikeUrlKey(key)) {
      next = redactReceiptSecretsInUrl(value.includes('?') ? maskDonorPiiInUrl(value) : value, receiptOptions);
    } else if ((key === '$set' || key === '$set_once') && value && typeof value === 'object') {
      next = maskDonorPiiInProperties(value, receiptOptions);
    } else if (key === '$heatmap_data' && value && typeof value === 'object') {
      // `$$heatmap` events key their click buffer by the raw page URL, so the
      // URL sits in a KEY, not a value (CON-261 review: a receipt code there).
      next = maskUrlKeys(value, receiptOptions);
    }
    if (next !== value) {
      out ||= { ...properties };
      out[key] = next;
    }
  }
  return out ?? properties;
}

/**
 * The `before_send` hook: scrubs the event's properties and its person-property
 * bags. Always returns an event — a failure inside the scrub must never drop or
 * break a capture, so the input comes back untouched on any error.
 */
export function scrubCaptureResult(captureResult, receiptOptions) {
  if (!captureResult) return captureResult;
  try {
    const properties = maskDonorPiiInProperties(captureResult.properties, receiptOptions);
    const $set = maskDonorPiiInProperties(captureResult.$set, receiptOptions);
    const $set_once = maskDonorPiiInProperties(captureResult.$set_once, receiptOptions);
    if (
      properties === captureResult.properties &&
      $set === captureResult.$set &&
      $set_once === captureResult.$set_once
    ) {
      return captureResult;
    }
    const out = { ...captureResult, properties };
    if ($set !== undefined) out.$set = $set;
    if ($set_once !== undefined) out.$set_once = $set_once;
    return out;
  } catch {
    return captureResult;
  }
}

/** The donor portal's `before_send`: the shared scrub plus its own host's receipt addresses. */
export function scrubPortalCaptureResult(captureResult) {
  return scrubCaptureResult(captureResult, portalScrubOptions());
}
